import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

interface PWAContextType {
  isOnline: boolean
  isInstallable: boolean
  isInstalled: boolean
  showInstallPrompt: () => void
  dismissInstallPrompt: () => void
  isUpdateAvailable: boolean
  updateApp: () => void
  connectionSpeed: 'slow' | 'fast' | 'offline'
  isStandalone: boolean
}

const PWAContext = createContext<PWAContextType | undefined>(undefined)

interface PWAProviderProps {
  children: ReactNode
}

export const PWAProvider: React.FC<PWAProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)
  const [connectionSpeed, setConnectionSpeed] = useState<'slow' | 'fast' | 'offline'>('fast')
  const [isStandalone, setIsStandalone] = useState(false)

  useEffect(() => {
    // Check if app is in standalone mode
    const checkStandalone = () => {
      const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone ||
        document.referrer.includes('android-app://')
      setIsStandalone(isStandaloneMode)
    }

    checkStandalone()

    // Network status listeners
    const handleOnline = () => {
      setIsOnline(true)
      setConnectionSpeed('fast')
    }

    const handleOffline = () => {
      setIsOnline(false)
      setConnectionSpeed('offline')
    }

    // Connection speed detection
    const checkConnectionSpeed = () => {
      if (!navigator.onLine) {
        setConnectionSpeed('offline')
        return
      }

      const connection = (navigator as any).connection
      if (connection) {
        const { effectiveType, downlink } = connection
        if (effectiveType === 'slow-2g' || effectiveType === '2g' || downlink < 1) {
          setConnectionSpeed('slow')
        } else {
          setConnectionSpeed('fast')
        }
      }
    }

    // Install prompt event listener
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setIsInstallable(true)
    }

    // App installed event listener
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsInstallable(false)
      setDeferredPrompt(null)
    }

    // Service Worker update listener
    const handleSWUpdate = () => {
      setIsUpdateAvailable(true)
    }

    // Register event listeners
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    // Connection monitoring
    checkConnectionSpeed()
    const connectionInterval = setInterval(checkConnectionSpeed, 30000) // Check every 30 seconds

    // Service Worker registration and update handling
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATE') {
          handleSWUpdate()
        }
      })
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
      clearInterval(connectionInterval)
    }
  }, [])

  const showInstallPrompt = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        setIsInstalled(true)
      }
      
      setDeferredPrompt(null)
      setIsInstallable(false)
    }
  }

  const dismissInstallPrompt = () => {
    setIsInstallable(false)
    setDeferredPrompt(null)
  }

  const updateApp = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration && registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' })
          window.location.reload()
        }
      })
    }
  }

  const value: PWAContextType = {
    isOnline,
    isInstallable,
    isInstalled,
    showInstallPrompt,
    dismissInstallPrompt,
    isUpdateAvailable,
    updateApp,
    connectionSpeed,
    isStandalone,
  }

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  )
}

export const usePWA = (): PWAContextType => {
  const context = useContext(PWAContext)
  if (context === undefined) {
    throw new Error('usePWA must be used within a PWAProvider')
  }
  return context
}